import type { ICustomField } from '@delasport/rule-engine-ui';
import type { IOption } from '@shared/form/types';
import type { Optional } from '@types';

import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';

import i18n from '@features/i18n';

type ResponseKey = 'code' | 'name' | 'title' | 'label' | 'displayName';

interface IResponseToSelectOptions {
  withId?: boolean;
  key: ResponseKey;
  withTranslation?: boolean;
}

export function responseToSelectOptions<T>(
  response: T,
  options?: Optional<IResponseToSelectOptions>
): IOption[] {
  if (isEmpty(response)) {
    return [];
  }

  if (Array.isArray(response)) {
    return response.map((item) => {
      let label: string = item;

      if (options?.withTranslation && item.translationKey) {
        label = i18n.t(item.translationKey as string, {
          ns: 'serverTranslation',
        });
      } else if (options?.key) {
        label = `${item[options.key]}`;
      }

      if (options?.key && options.withId) {
        label = `${label} (${item.id})`.trim();
      }

      return {
        label,
        value: item.id ?? item,
        ...(item.activationAction && {
          activationAction: item.activationAction,
        }),
      };
    });
  }

  if (isObject(response)) {
    return Object.entries(response).map(([key]) => ({
      label: key,
      value: key,
    }));
  }

  return [];
}

export const getTranslatedRuleEngineFields = (
  fields: ICustomField[]
): ICustomField[] =>
  fields.length > 0
    ? fields
        .map((field) => ({
          ...field,
          label: field.translationKey
            ? i18n.t(field.translationKey, { ns: 'serverTranslation' })
            : (field.label as string),
          values: field.values?.map((value) => ({
            ...value,
            label: value.translationKey
              ? i18n.t(value.translationKey, { ns: 'serverTranslation' })
              : value.label,
          })),
          operators: field.operators?.map((operator) => ({
            ...operator,
            label: operator.translationKey
              ? i18n.t(operator.translationKey, { ns: 'serverTranslation' })
              : operator.label,
          })),
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    : [];
